import React, {type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

const BAR_SIDE_PADDING = 8;
const BAR_AREA_HEIGHT = 116;
const TOOLTIP_GAP = 8;
const TOOLTIP_ARROW_SAFE_PADDING = 12;
const X_TICK_COUNT = 4;

interface TooltipData {
  time: string;
  value: string;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
}

export interface DailyBarThinGraphYAxis {
  maxValue: number;
  guideValues: number[];
}
const DEFAULT_MAX_Y_AXIS_VALUE = 10;
export function createDailyBarThinGraphYAxisFromScores(scores: number[]): DailyBarThinGraphYAxis {
  const maxScore = scores.reduce((candidate, score) => {
    if (!Number.isFinite(score)) {
      return candidate;
    }

    return Math.max(candidate, score, DEFAULT_MAX_Y_AXIS_VALUE);
  }, 0);
  const maxValue = maxScore === 0 ? 0 : getNiceMaxValue(maxScore);

  return {
    maxValue,
    guideValues: [maxValue, maxValue === 0 ? 0 : maxValue / 2],
  };
}

interface DailyBarThinGraphProps {
  data: Array<{
    score: number;
    scoreDate: Date;
  }>;
  yAxis?: DailyBarThinGraphYAxis;
  unit?: string;

  getTooltipData(selectedDataIndex: number): TooltipData;

  formatXAxisValue?(value: Date): string;

  formatYAxisValue?(value: number): string;
}

function getDefaultSelectedDataIndex(scores: number[]) {
  for (let index = scores.length - 1; index >= 0; index -= 1) {
    if (scores[index] > 0) {
      return index;
    }
  }

  return null;
}

function getEquidistantIndexes(length: number, count: number) {
  if (length <= 0 || count <= 0) {
    return [];
  }

  if (count <= 1 || length === 1) {
    return [0];
  }

  const lastIndex = length - 1;

  return Array.from({length: Math.min(length, count)}, (_, index) => Math.round((index * lastIndex) / (Math.min(length, count) - 1)));
}

function getNiceMaxValue(maxValue: number) {
  if (maxValue <= 0) {
    return 0;
  }
  return Math.ceil(maxValue / 10) * 10;
}

function getChartTop(value: number, chartMaxValue: number) {
  if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(chartMaxValue) || chartMaxValue <= 0) {
    return 0;
  }

  return Math.round((1 - Math.min(value / chartMaxValue, 1)) * BAR_AREA_HEIGHT);
}

export default function DailyBarThinGraph({
  data,
  getTooltipData,
  formatXAxisValue,
  formatYAxisValue = (value) => formatNumberWithCommas(value),
  yAxis,
  unit,
}: DailyBarThinGraphProps) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const barThinRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [barsWidth, setBarsWidth] = useState(window.innerWidth - 90);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  const scores = useMemo(() => data.map(({score}) => (Number.isFinite(score) ? Math.max(score, 0) : 0)), [data]);
  const [selectedDataIndex, setSelectedDataIndex] = useState<number | null>(() => getDefaultSelectedDataIndex(scores));
  const defaultYAxis = useMemo(() => createDailyBarThinGraphYAxisFromScores(scores), [scores]);
  const resolvedYAxis = useMemo(() => {
    if (!yAxis) {
      return defaultYAxis;
    }

    const maxValue = Number.isFinite(yAxis.maxValue) && yAxis.maxValue > 0 ? yAxis.maxValue : 0;
    const guideValues = yAxis.guideValues.filter((value) => Number.isFinite(value) && value >= 0);

    return {
      maxValue,
      guideValues: guideValues.length > 0 ? guideValues : [maxValue, maxValue === 0 ? 0 : maxValue / 2],
    };
  }, [defaultYAxis, yAxis]);
  const yAxisMaxValue = resolvedYAxis.maxValue;
  const yAxisGuideValues = resolvedYAxis.guideValues;
  const xTickIndexes = useMemo(() => getEquidistantIndexes(data.length, X_TICK_COUNT), [data.length]);
  const chartDataKey = useMemo(() => data.map(({score, scoreDate}) => `${scoreDate.getTime()}:${score}`).join('|'), [data]);

  const barHeights = useMemo(() => {
    if (yAxisMaxValue === 0) {
      return scores.map(() => 0);
    }

    return scores.map((score) => Math.min((score / yAxisMaxValue) * BAR_AREA_HEIGHT, BAR_AREA_HEIGHT));
  }, [scores, yAxisMaxValue]);

  const barLeftPositions = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    if (data.length === 1) {
      return [BAR_SIDE_PADDING];
    }

    const availableWidth = Math.max(barsWidth - BAR_SIDE_PADDING * 2, 0);

    return data.map((_, index) => BAR_SIDE_PADDING + (index * availableWidth) / (data.length - 1));
  }, [barsWidth, data]);

  const selectedTooltipData = selectedDataIndex !== null && scores[selectedDataIndex] > 0 ? getTooltipData(selectedDataIndex) : null;
  const hasSelectedTooltipData = selectedTooltipData !== null;
  const selectedTooltipKey = selectedTooltipData ? `${selectedTooltipData.time}|${selectedTooltipData.value}` : '';
  const getXAxisValue = (scoreDate: Date) =>
    formatXAxisValue ? formatXAxisValue(scoreDate) : CldrDatetimeFormatter.format(scoreDate, 'short');

  useEffect(() => {
    setSelectedDataIndex(getDefaultSelectedDataIndex(scores));
  }, [chartDataKey, scores]);

  useEffect(() => {
    function measureBarsWidth() {
      if (barsRef.current) {
        setBarsWidth(barsRef.current.clientWidth);
      }
    }

    measureBarsWidth();
    window.addEventListener('resize', measureBarsWidth);

    return () => {
      window.removeEventListener('resize', measureBarsWidth);
    };
  }, []);

  useLayoutEffect(() => {
    if (!hasSelectedTooltipData || !barThinRef.current || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const activeSelectedDataIndex = selectedDataIndex;
    const barThinElement = barThinRef.current;
    const tooltipElement = tooltipRef.current;

    function updateTooltipPosition() {
      const selectedBar = barThinElement.querySelector<HTMLElement>(`[data-bar-index="${activeSelectedDataIndex}"]`);
      const chartMain = barThinElement.querySelector<HTMLElement>('.bar_thin_main');
      if (!selectedBar || !chartMain) {
        setTooltipPosition(null);
        return;
      }

      const containerRect = barThinElement.getBoundingClientRect();
      const chartMainRect = chartMain.getBoundingClientRect();
      const selectedBarTarget = selectedBar.querySelector<HTMLElement>('.bar_thin_fill') ?? selectedBar;
      const selectedBarRect = selectedBarTarget.getBoundingClientRect();
      const {width: tooltipWidth, height: tooltipHeight} = tooltipElement.getBoundingClientRect();
      const barCenter = selectedBarRect.left - containerRect.left + selectedBarRect.width / 2;
      const chartMainLeft = chartMainRect.left - containerRect.left;
      const chartMainRight = chartMainRect.right - containerRect.left;
      const overflowLeft = Math.max(TOOLTIP_ARROW_SAFE_PADDING - (barCenter - chartMainLeft), 0);
      const overflowRight = Math.max(TOOLTIP_ARROW_SAFE_PADDING - (chartMainRight - barCenter), 0);
      const minTooltipCenter = chartMainLeft + tooltipWidth / 2 - overflowLeft;
      const maxTooltipCenter = chartMainRight - tooltipWidth / 2 + overflowRight;
      const fallbackTooltipCenter = chartMainLeft + chartMainRect.width / 2;
      const clampedTooltipCenter = Math.min(Math.max(barCenter, minTooltipCenter), maxTooltipCenter);
      const tooltipLeft = minTooltipCenter <= maxTooltipCenter ? clampedTooltipCenter : fallbackTooltipCenter;
      const tooltipTop = selectedBarRect.top - containerRect.top - tooltipHeight - TOOLTIP_GAP;
      const unclampedArrowLeft = barCenter - (tooltipLeft - tooltipWidth / 2);
      const arrowLeft = Math.min(Math.max(unclampedArrowLeft, TOOLTIP_ARROW_SAFE_PADDING), tooltipWidth - TOOLTIP_ARROW_SAFE_PADDING);

      setTooltipPosition({
        top: tooltipTop,
        left: tooltipLeft,
        arrowLeft,
      });
    }

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [barsWidth, chartDataKey, hasSelectedTooltipData, selectedDataIndex, selectedTooltipKey]);

  const tooltipStyle = {
    display: 'inline-flex',
    top: `${tooltipPosition?.top ?? 0}px`,
    left: `${tooltipPosition?.left ?? 0}px`,
    visibility: tooltipPosition ? 'visible' : 'hidden',
    '--ev-arrow-left': `${tooltipPosition?.arrowLeft ?? 0}px`,
  } as CSSProperties;

  return (
    <div
      ref={barThinRef}
      className="bar_thin"
      data-mode="date"
      data-unit={unit}
    >
      <div className="bar_thin_y_axis">
        {yAxisGuideValues.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="bar_thin_y_label"
            style={{top: `${getChartTop(value, yAxisMaxValue)}px`}}
          >
            {formatYAxisValue(value)}
          </span>
        ))}
      </div>
      <div className="bar_thin_main">
        {yAxisGuideValues.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="bar_thin_line"
            style={{top: `${getChartTop(value, yAxisMaxValue)}px`}}
            aria-hidden="true"
          />
        ))}
        <div
          className="bar_thin_bars"
          ref={barsRef}
        >
          {data.map((item, index) => {
            const tooltipData = getTooltipData(index);
            const isSelectable = scores[index] > 0;
            const isSelected = isSelectable && index === selectedDataIndex;

            return (
              <div
                key={`${item.scoreDate.toISOString()}-${index}`}
                className={clsx('bar_thin_item', {
                  'is-active': isSelected,
                })}
                data-bar-index={index}
                data-date={getXAxisValue(item.scoreDate)}
                data-value={scores[index]}
                style={{
                  left: `${barLeftPositions[index]}px`,
                  cursor: isSelectable ? undefined : 'default',
                }}
                role={isSelectable ? 'button' : undefined}
                tabIndex={isSelectable ? 0 : -1}
                aria-label={isSelectable ? `${tooltipData.time} ${tooltipData.value}` : undefined}
                aria-disabled={!isSelectable || undefined}
                onClick={() => {
                  if (isSelectable) {
                    setSelectedDataIndex(index);
                  }
                }}
                onKeyDown={(event) => {
                  if (isSelectable && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    setSelectedDataIndex(index);
                  }
                }}
              >
                <span
                  className="bar_thin_fill"
                  style={{height: `${barHeights[index]}px`}}
                />
              </div>
            );
          })}
        </div>
        <div
          className="bar_thin_x_axis"
          aria-hidden="true"
        >
          {xTickIndexes.map((index) => (
            <span
              key={`${data[index].scoreDate.toISOString()}-${index}`}
              className="bar_thin_x_label"
            >
              {getXAxisValue(data[index].scoreDate)}
            </span>
          ))}
        </div>
      </div>
      {selectedTooltipData && (
        <div
          ref={tooltipRef}
          className="speech_bubble bar_thin_tooltip"
          role="tooltip"
          style={tooltipStyle}
        >
          <span className="time">{selectedTooltipData.time}</span>
          <span className="wattage">{selectedTooltipData.value}</span>
        </div>
      )}
    </div>
  );
}
